import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useClientConfig } from '../lib/clientConfig.js';
import {
  closeActivitySession,
  flushActivityQueue,
  initializeActivityCapture,
  prepareActivityIdentityTransition,
  recordActivityEvent,
  updateActivitySession,
} from '../lib/activityCapture.js';
import { usePageMeta } from './PageMetaContext.jsx';
import { useOptionalCurrentUser } from './CurrentUserContext.jsx';

function routeReference(location) {
  return `${location.pathname}${location.search || ''}`;
}

// Bounded: activity_events.target_label is NVARCHAR(512) (migration 006), and an
// uncapped label from reporter-authored content (typed-intake titles now reach
// queue cards) makes the capture INSERT throw "String or binary data would be
// truncated" server-side. 500 leaves headroom under the column bound.
const TARGET_LABEL_MAX_LENGTH = 500;

function trimLabel(value) {
  const text = `${value || ''}`.replace(/\s+/g, ' ').trim();
  if (!text) return null;
  return text.length > TARGET_LABEL_MAX_LENGTH
    ? `${text.slice(0, TARGET_LABEL_MAX_LENGTH - 1)}…`
    : text;
}

function labelForInput(element) {
  if (!element) return null;
  const ariaLabel = trimLabel(element.getAttribute('aria-label'));
  if (ariaLabel) return ariaLabel;
  if (element.labels?.length) {
    return trimLabel(
      Array.from(element.labels)
        .map((label) => label.textContent)
        .join(' '),
    );
  }
  if (element.id) {
    const label = document.querySelector(`label[for="${element.id}"]`);
    if (label) return trimLabel(label.textContent);
  }
  return trimLabel(element.placeholder) || trimLabel(element.name) || trimLabel(element.id);
}

function resolveSemanticTarget(start) {
  if (!(start instanceof Element)) return null;
  const target = start.closest(
    '[data-activity-id],[data-activity-label],[data-activity-type],button,a,input,select,textarea,form,[role="button"],[role="tab"],[role="switch"]',
  );
  if (!target) return null;
  const dataset = target.dataset || {};
  const tagName = target.tagName.toLowerCase();
  const role = target.getAttribute('role');
  const targetType =
    dataset.activityType ||
    (role === 'tab' ? 'tab' : role === 'switch' ? 'toggle' : tagName === 'a' ? 'link' : tagName);
  const targetLabel =
    trimLabel(dataset.activityLabel) ||
    trimLabel(target.getAttribute('aria-label')) ||
    trimLabel(target.textContent) ||
    labelForInput(target);
  const targetId = dataset.activityId || target.id || target.getAttribute('name') || null;
  return {
    element: target,
    targetType,
    targetId,
    targetLabel,
  };
}

function buildInputPayload(target) {
  const base = {
    source: 'document',
    targetType: 'input',
    targetId: target.id || target.name || null,
    targetLabel: labelForInput(target),
    fieldName: target.name || target.id || null,
  };

  if (target.type === 'password') return null;

  if (target.type === 'file') {
    const files = Array.from(target.files || []).map((file) => ({
      name: file.name,
      type: file.type,
      size: file.size,
    }));
    return {
      ...base,
      eventType: 'ui',
      eventName: 'ui.input.change',
      valueText: files.map((file) => file.name).join(', ') || null,
      valueJson: {
        fileCount: files.length,
        files,
      },
    };
  }

  return {
    ...base,
    eventType: 'ui',
    eventName: 'ui.input.change',
    valueText: target.value,
  };
}

function buildChangePayload(target) {
  if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return null;

  const base = {
    source: 'document',
    targetId: target.id || target.name || null,
    targetLabel: labelForInput(target),
    fieldName: target.name || target.id || null,
  };

  if (target instanceof HTMLSelectElement) {
    const selectedOption = target.selectedOptions?.[0] || null;
    return {
      ...base,
      eventType: 'ui',
      eventName: 'ui.select.change',
      targetType: 'select',
      valueText: target.value,
      valueJson: {
        value: target.value,
        label: selectedOption ? trimLabel(selectedOption.textContent) : null,
      },
    };
  }

  if (target.type === 'checkbox' || target.type === 'radio') {
    return {
      ...base,
      eventType: 'ui',
      eventName: 'ui.toggle',
      targetType: target.type,
      valueText: `${target.checked}`,
      valueJson: {
        checked: target.checked,
        value: target.value || null,
      },
    };
  }

  if (target.type === 'file') {
    return buildInputPayload(target);
  }

  return null;
}

export function ActivityCaptureProvider({ children }) {
  const location = useLocation();
  const { meta } = usePageMeta();
  const clientConfig = useClientConfig();
  const user = useOptionalCurrentUser()?.user || null;
  const activityConfig = clientConfig.flags?.activityCapture || null;
  const enabled = !clientConfig.loading && !!activityConfig;
  const fullRoute = routeReference(location);
  const actorMarker = user?.id || null;
  const previousActorRef = useRef(actorMarker);

  // Fallback identity-transition detector: the primary barrier runs inside
  // CurrentUserContext BEFORE each auth mutation, but any flow that changed
  // the actor without passing through it (e.g. a full-page login bounce that
  // somehow preserved sessionStorage) still must not continue the previous
  // actor's capture session.
  useEffect(() => {
    const previous = previousActorRef.current;
    previousActorRef.current = actorMarker;
    if (!enabled) return;
    if (previous && actorMarker && previous !== actorMarker) {
      prepareActivityIdentityTransition();
    }
  }, [actorMarker, enabled]);

  useEffect(() => {
    if (!enabled) return;
    initializeActivityCapture({
      config: activityConfig,
      initialRoute: fullRoute,
      lastRoute: fullRoute,
      routePath: location.pathname,
      routeTitle: meta?.title || null,
      actorMarker,
      payload: {
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      },
    });
  }, [activityConfig, enabled, fullRoute, location.pathname, meta?.title, actorMarker]);

  useEffect(() => {
    if (!enabled) return;
    updateActivitySession({ lastRoute: fullRoute });
    recordActivityEvent({
      eventType: 'route',
      eventName: 'route.view',
      source: 'shell',
      routePath: location.pathname,
      routeTitle: meta?.title || null,
      payload: {
        route: fullRoute,
      },
    });
  }, [enabled, fullRoute, location.pathname, meta?.title]);

  useEffect(() => {
    if (!enabled) return;

    const handleClick = (event) => {
      const target = resolveSemanticTarget(event.target);
      if (!target) return;
      recordActivityEvent({
        eventType: 'ui',
        eventName: 'ui.click',
        source: 'document',
        targetType: target.targetType,
        targetId: target.targetId,
        targetLabel: target.targetLabel,
      });
    };

    const handleInput = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
      if (
        target instanceof HTMLInputElement &&
        ['checkbox', 'radio', 'file', 'hidden', 'password'].includes(target.type)
      ) {
        return;
      }
      const payload = buildInputPayload(target);
      if (payload) recordActivityEvent(payload);
    };

    const handleChange = (event) => {
      const payload = buildChangePayload(event.target);
      if (payload) recordActivityEvent(payload);
    };

    const handleSubmit = (event) => {
      if (!(event.target instanceof HTMLFormElement)) return;
      recordActivityEvent({
        eventType: 'form',
        eventName: 'form.submit',
        source: 'document',
        targetType: 'form',
        targetId: event.target.id || event.target.getAttribute('name') || null,
        targetLabel:
          trimLabel(event.target.getAttribute('aria-label')) ||
          trimLabel(event.target.getAttribute('name')) ||
          null,
      });
    };

    const handlePageHide = () => {
      closeActivitySession({
        routePath: window.location.pathname,
        routeTitle: meta?.title || null,
      });
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('input', handleInput, true);
    document.addEventListener('change', handleChange, true);
    document.addEventListener('submit', handleSubmit, true);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('input', handleInput, true);
      document.removeEventListener('change', handleChange, true);
      document.removeEventListener('submit', handleSubmit, true);
      window.removeEventListener('pagehide', handlePageHide);
      void flushActivityQueue();
    };
  }, [enabled, meta?.title]);

  return children;
}
